/**
 * Pincode Checker with Metaobject Integration
 * Handles pincode validation, local storage, and metaobject data fetching
 */

class PincodeChecker {
  constructor() {
    this.STORAGE_KEY = 'userPincode';
    this.PINCODE_DATA_KEY = 'pincodeData';
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.pincodeData = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.container = document.querySelector('[data-pincode-checker]');
    if (!this.container) return;

    this.elements = {
      dataContainer: this.container.querySelector('.pincode-data'),
      inputSection: this.container.querySelector('[data-input-section]'),
      resultSection: this.container.querySelector('[data-result-section]'),
      loadingSection: this.container.querySelector('[data-pincode-loading]'),
      input: this.container.querySelector('[data-pincode-input]'),
      submitBtn: this.container.querySelector('[data-pincode-submit]'),
      changeBtn: this.container.querySelector('[data-pincode-change]'),
      error: this.container.querySelector('[data-pincode-error]'),
      resultIcon: this.container.querySelector('[data-result-icon]'),
      resultLocation: this.container.querySelector('[data-result-location]'),
      resultWarehouse: this.container.querySelector('[data-result-warehouse]'),
      resultMessage: this.container.querySelector('[data-result-message]'),
      pincodeValue: this.container.querySelector('[data-pincode-value]')
    };

    this.config = {
      metaobjectId: this.elements.dataContainer.dataset.metaobjectId,
      productId: this.elements.dataContainer.dataset.productId,
      productInstantDelivery: this.elements.dataContainer.dataset.productInstantDelivery === 'true',
      productAvailable: this.elements.dataContainer.dataset.productAvailable === 'true',
      instantDeliveryMessage: this.elements.dataContainer.dataset.instantDeliveryMessage,
      standardDeliveryMessage: this.elements.dataContainer.dataset.standardDeliveryMessage,
      unavailableMessage: this.elements.dataContainer.dataset.unavailableMessage,
      notEligibleMessage: this.elements.dataContainer.dataset.notEligibleMessage,
      outOfStockMessage: this.elements.dataContainer.dataset.outOfStockMessage,
      enableTimeBased: this.elements.dataContainer.dataset.enableTimeBased === 'true',
      deliveryStartHour: parseInt(this.elements.dataContainer.dataset.deliveryStartHour) || 8,
      deliveryEndHour: parseInt(this.elements.dataContainer.dataset.deliveryEndHour) || 19,
      morningSlotEnd: parseInt(this.elements.dataContainer.dataset.morningSlotEnd) || 10,
      weekdayMorningEdd: this.elements.dataContainer.dataset.weekdayMorningEdd,
      weekdayDayEdd: this.elements.dataContainer.dataset.weekdayDayEdd,
      weekdayNightMessage: this.elements.dataContainer.dataset.weekdayNightMessage,
      weekendMessage: this.elements.dataContainer.dataset.weekendMessage,
      timeRestrictedMessage: this.elements.dataContainer.dataset.timeRestrictedMessage,
      enableWeekends: this.elements.dataContainer.dataset.enableWeekends === 'true'
    };

    this.attachEventListeners();
    this.checkStoredPincode();
  }

  attachEventListeners() {
    // Submit button click
    this.elements.submitBtn.addEventListener('click', () => this.handleSubmit());

    // Enter key on input
    this.elements.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.handleSubmit();
      }
    });

    // Input validation - only numbers
    this.elements.input.addEventListener('input', (e) => {
      e.target.value = e.target.value.replace(/[^0-9]/g, '');
      this.hideError();
    });

    // Change pincode button
    this.elements.changeBtn.addEventListener('click', () => this.resetForm());
  }

  async handleSubmit() {
    const pincode = this.elements.input.value.trim();

    // Validate pincode
    if (!this.validatePincode(pincode)) {
      this.showError('Please enter a valid 6-digit pincode');
      return;
    }

    this.showLoading();
    
    try {
      // Fetch pincode data from metaobject
      await this.fetchPincodeData();
      
      // Check if pincode is available
      const result = this.checkPincodeAvailability(pincode);
      
      // Save to local storage
      this.savePincodeToStorage(pincode, result);
      
      // Display result
      this.displayResult(pincode, result);
    } catch (error) {
      console.error('Pincode check error:', error);
      this.showError('Unable to check pincode availability. Please try again.');
      this.hideLoading();
    }
  }

  validatePincode(pincode) {
    return /^\d{6}$/.test(pincode);
  }

  async fetchPincodeData() {
    // Check if we have cached data
    const cachedData = this.getCachedPincodeData();
    if (cachedData) {
      this.pincodeData = cachedData;
      return;
    }

    // Use the pre-loaded data from Liquid template
    if (window.pincodeCheckerData && window.pincodeCheckerData.pincodes) {
      this.pincodeData = window.pincodeCheckerData.pincodes;
      this.cachePincodeData(this.pincodeData);
      return;
    }

    // Fallback: Try to use cached data even if expired
    const expiredCache = this.getCachedPincodeData(true);
    if (expiredCache) {
      this.pincodeData = expiredCache;
    } else {
      throw new Error('No pincode data available. Please configure the metaobject in theme settings.');
    }
  }

  parsePincodeData(metaobjectData) {
    // Parse the metaobject data structure
    // Adjust this based on your actual metaobject structure
    const pincodeMap = {};
    
    if (metaobjectData && metaobjectData.metaobject) {
      const fields = metaobjectData.metaobject.fields;
      
      // Assuming the metaobject has fields like:
      // - pincodes: list of pincodes
      // - locations: corresponding locations
      fields.forEach(field => {
        if (field.key === 'pincodes' && field.value) {
          const pincodes = JSON.parse(field.value);
          pincodes.forEach((item, index) => {
            pincodeMap[item.pincode] = {
              location: item.location || 'Available',
              available: true
            };
          });
        }
      });
    }
    
    return pincodeMap;
  }

  checkPincodeAvailability(pincode) {
    // First check if product is in stock
    if (!this.config.productAvailable) {
      return {
        available: false,
        location: null,
        message: this.config.outOfStockMessage,
        deliveryType: 'out_of_stock',
        eddTime: null,
        isProductIssue: true
      };
    }

    // Check time and day eligibility if enabled
    const timeCheck = this.config.enableTimeBased ? this.checkTimeEligibility() : { eligible: true, edd: null };
    
    // Check if pincode exists in metaobject data (Mumbai/Instant delivery areas)
    const isPincodeInMetaobject = this.pincodeData && this.pincodeData[pincode];
    
    // Logic for delivery type:
    // 1. If pincode is in metaobject AND product has instant delivery enabled → Instant Delivery
    // 2. If pincode is NOT in metaobject OR product doesn't have instant delivery → Standard Delivery
    
    if (isPincodeInMetaobject && this.config.productInstantDelivery) {
      // Mumbai pincode + Product eligible for instant delivery
      // But check if time is eligible
      if (!timeCheck.eligible) {
        // Time not eligible - show standard delivery with time message
        return {
          available: true,
          location: this.pincodeData[pincode].location,
          warehouse: this.pincodeData[pincode].warehouse,
          message: timeCheck.message,
          deliveryType: 'standard',
          eddTime: null,
          isProductIssue: false,
          timeRestricted: true
        };
      }
      
      return {
        available: true,
        location: this.pincodeData[pincode].location,
        warehouse: this.pincodeData[pincode].warehouse,
        message: this.config.instantDeliveryMessage,
        deliveryType: 'instant',
        eddTime: timeCheck.edd,
        isProductIssue: false,
        timeRestricted: false
      };
    } else {
      // Non-Mumbai pincode OR product not eligible for instant delivery → Standard Delivery
      return {
        available: true,
        location: isPincodeInMetaobject ? this.pincodeData[pincode].location : null,
        message: this.config.standardDeliveryMessage,
        deliveryType: 'standard',
        eddTime: null,
        isProductIssue: false,
        timeRestricted: false
      };
    }
  }

  checkTimeEligibility() {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    const hour = now.getHours();
    const minute = now.getMinutes();
    const currentTime = hour + (minute / 60);

    // Weekend check (Saturday = 6, Sunday = 0)
    if (!this.config.enableWeekends && (day === 0 || day === 6)) {
      return {
        eligible: false,
        message: this.config.weekendMessage,
        edd: null
      };
    }

    // Weekday time checks - use configurable hours
    const startHour = this.config.deliveryStartHour;
    const endHour = this.config.deliveryEndHour;
    const morningEnd = this.config.morningSlotEnd;

    // Before start hour or After end hour
    if (currentTime < startHour || currentTime >= endHour) {
      return {
        eligible: false,
        message: this.config.weekdayNightMessage,
        edd: null
      };
    }

    // Morning slot: start hour to morning slot end
    if (currentTime >= startHour && currentTime < morningEnd) {
      return {
        eligible: true,
        message: null,
        edd: this.config.weekdayMorningEdd
      };
    }

    // Day slot: morning slot end to end hour
    if (currentTime >= morningEnd && currentTime < endHour) {
      return {
        eligible: true,
        message: null,
        edd: this.config.weekdayDayEdd
      };
    }

    // Fallback
    return {
      eligible: false,
      message: this.config.weekdayNightMessage,
      edd: null
    };
  }

  displayResult(pincode, result) {
    this.hideLoading();
    this.elements.inputSection.style.display = 'none';
    this.elements.resultSection.style.display = 'block';

    // Set pincode value
    this.elements.pincodeValue.textContent = pincode;

    // Set icon based on delivery type
    let iconHTML = '';
    let iconClass = '';
    
    if (result.deliveryType === 'instant') {
      // Lightning bolt icon for instant delivery
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>';
      iconClass = 'success instant';
    } else if (result.deliveryType === 'standard') {
      // Truck icon for standard delivery
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';
      iconClass = 'success standard';
    } else {
      // X icon for out of stock
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
      iconClass = 'error';
    }
    
    this.elements.resultIcon.innerHTML = iconHTML;
    this.elements.resultIcon.className = `pincode-result-icon ${iconClass}`;

    // Set location
    if (result.location) {
      this.elements.resultLocation.textContent = result.location;
      this.elements.resultLocation.style.display = 'block';
    } else {
      this.elements.resultLocation.style.display = 'none';
    }

    // Set warehouse
    if (result.warehouse) {
      this.elements.resultWarehouse.innerHTML = '<span class="warehouse-label">📦 Warehouse:</span> <span class="warehouse-value">' + result.warehouse + '</span>';
      this.elements.resultWarehouse.style.display = 'block';
    } else {
      this.elements.resultWarehouse.style.display = 'none';
    }

    // Set message with delivery type badge and EDD
    let deliveryBadge = '';
    let eddDisplay = '';
    
    if (result.deliveryType === 'instant') {
      deliveryBadge = '<span class="delivery-badge instant">⚡ Instant Delivery</span>';
      if (result.eddTime) {
        eddDisplay = '<span class="edd-time">🕐 ' + result.eddTime + '</span>';
      }
    } else if (result.deliveryType === 'standard') {
      deliveryBadge = '<span class="delivery-badge standard">🚚 Standard Delivery</span>';
      if (result.timeRestricted) {
        eddDisplay = '<span class="time-info">⏰ ' + result.message + '</span>';
      }
    }
    
    const messageContent = result.timeRestricted ? '' : '<span>' + result.message + '</span>';
    
    this.elements.resultMessage.innerHTML = deliveryBadge + eddDisplay + messageContent;
    this.elements.resultMessage.className = `pincode-result-message ${result.deliveryType === 'out_of_stock' ? 'error' : 'success'} ${result.deliveryType}`;
  }

  resetForm() {
    this.elements.resultSection.style.display = 'none';
    this.elements.inputSection.style.display = 'block';
    this.elements.input.value = '';
    this.elements.input.focus();
  }

  checkStoredPincode() {
    const storedData = this.getStoredPincode();
    if (storedData && storedData.pincode) {
      // Auto-check the stored pincode
      this.elements.input.value = storedData.pincode;
      this.handleSubmit();
    }
  }

  savePincodeToStorage(pincode, result) {
    try {
      const data = {
        pincode: pincode,
        result: result,
        timestamp: Date.now()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  getStoredPincode() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        // Check if data is not too old (24 hours)
        if (Date.now() - parsed.timestamp < this.CACHE_DURATION) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return null;
  }

  cachePincodeData(data) {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.PINCODE_DATA_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching pincode data:', error);
    }
  }

  getCachedPincodeData(ignoreExpiry = false) {
    try {
      const cached = localStorage.getItem(this.PINCODE_DATA_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (ignoreExpiry || Date.now() - parsed.timestamp < this.CACHE_DURATION) {
          return parsed.data;
        }
      }
    } catch (error) {
      console.error('Error reading cached data:', error);
    }
    return null;
  }

  showLoading() {
    this.elements.inputSection.style.display = 'none';
    this.elements.loadingSection.style.display = 'flex';
  }

  hideLoading() {
    this.elements.loadingSection.style.display = 'none';
  }

  showError(message) {
    this.elements.error.textContent = message;
    this.elements.error.style.display = 'block';
  }

  hideError() {
    this.elements.error.style.display = 'none';
  }
}

// Initialize when script loads
new PincodeChecker();
